import {
    Alert,
    FormGroup,
    FormParagraph,
    FormSection,
    Table,
    TableBody,
    TableCell,
    TableCellExpandRow,
    TableContainer,
    TableRow,
    TableRowExpanded,
} from '@ksyos/design-system';
import { Fragment, useEffect, useState } from 'react';
import type { RespiAllerQuestionnaireCustomControlData } from '@common/validation/customControls/allergology/respiAllerQuestionnaire/v1/respiAllerQuestionnaireSchemaV1';
import type { IRespiAllerQuestionnaireStatusCustomControlData } from '@common/validation/customControls/allergology/respiAllerQuestionnaireStatusSchema';
import type { RespiAllerReferralCustomControlData } from '@common/validation/customControls/allergology/respiAllerReferral/v1/respiAllerReferralSchemaV1';
import { CustomControlCode, ProcessActivityStatus } from '@common/enums';
import { FhirCodeSystem } from '@common/enums/fhirCodeSystem';
import { getLanguage } from '@common/translate/translate';
import { getCustomControlData } from '@common/utils/processUtil';
import { ProcessContext } from '@frontend/components/pages/episodeCategory/carePath/process/processContext';
import { getProcessQuestionnaireResponses } from '@frontend/services/api/processesApiService';
import { getProcessQuestionnaireResponse } from '@frontend/services/api/processQuestionnaireResponsesApiService';
import { getQuestionnaireItems } from '@frontend/services/api/questionnairesApiService';
import { useProcessActivityReadContext } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';

type Questionnaire = {
    categories: QuestionnaireCategory[];
};

type QuestionnaireCategory = {
    code: string;
    display: string;
    questions: QuestionnaireQuestion[];
};

type QuestionnaireQuestion = {
    answer: QuestionnaireQuestionValue;
    text: string;
};

type QuestionnaireQuestionValue = {
    value: string;
};

const customTableRowStyles = {
    '&:hover td': {
        backgroundColor: '#F4F4F2 !important',
    },
};

const customTableCellStyles = {
    backgroundColor: '#FAF9F8',
    borderTop: '4px solid #fff',
};

export function RespiAllerQuestionnaireRead() {
    const { process } = useDefinedContext(ProcessContext);
    const {
        processActivity: {
            customControlData: { processQuestionnaireResponseId },
        },
    } = useProcessActivityReadContext<RespiAllerQuestionnaireCustomControlData>();
    const translate = getLanguage();
    const abortSignal = useAbortSignal();
    const [questionnaireData, setQuestionnaireData] = useState<Questionnaire>({ categories: [] });
    const [questionnaireCollapsible, setQuestionnaireCollapsible] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (processQuestionnaireResponseId) {
            getQuestionnaireResponse(process.id, processQuestionnaireResponseId);
        }
    }, [process.id, processQuestionnaireResponseId]);

    const referralCustomControlData = getCustomControlData<RespiAllerReferralCustomControlData>(
        process,
        CustomControlCode.RespiAllerReferral,
        [ProcessActivityStatus.complete]
    );

    const questionnaireCustomControlData = getCustomControlData<IRespiAllerQuestionnaireStatusCustomControlData>(
        process,
        CustomControlCode.RespiAllerQuestionnaireStatus,
        [ProcessActivityStatus.complete, ProcessActivityStatus.draft]
    );

    const emailAddress = referralCustomControlData?.patient.emailAddress ?? '';

    return (
        <>
            <FormSection>
                <FormGroup label={translate.patient.patientEmailAddress}>
                    <FormParagraph>{emailAddress ?? ''}</FormParagraph>
                </FormGroup>
            </FormSection>
            <FormSection>
                {!processQuestionnaireResponseId && !questionnaireCustomControlData?.status && (
                    <Alert appearance={'info'}>
                        Er zijn nog geen gegevens ontvangen. De vragenlijst is verstuurd naar{' '}
                        <Alert.Link
                            href={`mailto:${emailAddress}`}
                            variant="externalLink"
                        >
                            {emailAddress}
                        </Alert.Link>
                        , als dit e-mailadres niet klopt, klik dan op &quot;Herzenden vragenlijst&quot;
                    </Alert>
                )}
                {!processQuestionnaireResponseId && questionnaireCustomControlData?.status === 'draft' && (
                    <Alert appearance={'info'}>
                        De patiënt heeft de vragenlijst gedeeltelijk ingevuld en opgeslagen, maar nog niet verstuurd
                    </Alert>
                )}

                <TableContainer>
                    <Table>
                        <TableBody>
                            {questionnaireData.categories.map((category) => (
                                <Fragment key={category.code}>
                                    <TableRow
                                        onClick={() =>
                                            setQuestionnaireCollapsible((current) => ({
                                                ...current,
                                                [category.code]: !current[category.code],
                                            }))
                                        }
                                        sx={{ ...customTableRowStyles }}
                                    >
                                        <TableCellExpandRow
                                            isExpandable
                                            isExpanded={questionnaireCollapsible[category.code]}
                                            onExpand={() => undefined}
                                            sx={{ ...customTableCellStyles }}
                                        />

                                        <TableCell
                                            sx={{
                                                fontWeight: 'medium',
                                                ...customTableCellStyles,
                                            }}
                                        >
                                            {category.display}
                                        </TableCell>
                                    </TableRow>

                                    <TableRowExpanded isExpanded={questionnaireCollapsible[category.code]}>
                                        <TableCell colSpan={2}>
                                            {category.questions.map((question) => (
                                                <FormGroup
                                                    key={question.text}
                                                    label={question.text}
                                                >
                                                    <FormParagraph>{question.answer.value}</FormParagraph>
                                                </FormGroup>
                                            ))}
                                        </TableCell>
                                    </TableRowExpanded>
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </FormSection>
        </>
    );

    async function getQuestionnaireResponse(questionnaireProcessId: string, responseId: string): Promise<void> {
        const response = await getProcessQuestionnaireResponse(responseId, undefined, abortSignal);
        const questionnaireItems = await getQuestionnaireItems(
            response.questionnaireId,
            { includeCodes: true },
            abortSignal
        );
        const questionnaireResponses = await getProcessQuestionnaireResponses(
            questionnaireProcessId,
            undefined,
            abortSignal
        );

        const data: Questionnaire = {
            categories: [],
        };

        for (const questionnaireResponse of questionnaireResponses.items) {
            for (const responseItem of questionnaireResponse?.items ?? []) {
                const questionnaireItem = questionnaireItems.items.find((entry) => entry.id === responseItem.linkId);

                if (!questionnaireItem) {
                    continue;
                }

                const category = questionnaireItem.code?.find(
                    (entry) => entry.system === FhirCodeSystem.QuestionnaireItemCategory
                );

                if (!category) {
                    continue;
                }

                let currentCategory = data.categories.find((entry) => entry.code === category.code);

                if (!currentCategory) {
                    currentCategory = {
                        code: category.code,
                        display: category.display,
                        questions: [],
                    };

                    data.categories = [...data.categories, currentCategory];
                }

                currentCategory.questions = [
                    ...currentCategory.questions,
                    {
                        answer: {
                            value: responseItem.answer.value,
                        },
                        text: responseItem.text,
                    },
                ];
            }
        }

        setQuestionnaireData(data);
    }
}
